/**
 * Keeps the assembly-instruction download links in sync with the selected variant.
 *
 * The link lives in a product accordion row, and the accordion is a child of the
 * `_product-media` block. `sections/main-product.liquid` skips that whole block in
 * the `product.hdt_ajax` view, so the markup Athora fetches after a variant change
 * does not contain the row at all — neither the hard-coded block list in
 * `global.js` (`update_product_info_fn`) nor the `detail.html` trick used by
 * `lead-time-property.js` can reach it.
 *
 * So the row ships its own variant-id → URL list map and rebuilds the links locally.
 * A variant can have several PDFs (`custom.montageanleitung` is a `list.file_reference`);
 * a plain string is still accepted, it is what the map held before the list type.
 * The map is small (one entry per variant that has an instruction) and needs no request.
 *
 * Loaded as a module from `blocks/_product-accordion-item.liquid`; module scripts
 * with the same URL are evaluated once per document, so a second accordion row does
 * not register the listener twice.
 *
 * Documented in `materials/altdecor-docs/product-accordion.md`.
 */

const ROW_SELECTOR = '[data-instructions-row]';

const renderLinks = (variantId) => {
  for (const row of document.querySelectorAll(ROW_SELECTOR)) {
    const box = row.querySelector('[data-instructions-links]');
    const source = row.querySelector('[data-instructions-map]');
    if (!box || !source) continue;

    let map = {};
    try {
      map = JSON.parse(source.textContent || '{}');
    } catch {
      // A broken map must not take the whole accordion row down with it: the
      // static text above the links is the part the customer always needs.
      continue;
    }

    const urls = [map[String(variantId)] ?? []].flat();
    const links = [];
    for (const [index, url] of urls.entries()) {
      const link = document.createElement('a');
      link.className = 'hdt-link';
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.download = '';
      link.textContent =
        urls.length > 1 ? box.dataset.labelPart.replace('__N__', String(index + 1)) : box.dataset.label;
      const paragraph = document.createElement('p');
      paragraph.append(link);
      links.push(paragraph);
    }
    // No instruction for this variant: hide the box instead of leaving the
    // previous variant's files, which is the failure this whole file exists for.
    box.replaceChildren(...links);
    box.hidden = links.length === 0;
  }
};

const syncInstructionLink = (event) => {
  const variantId = event.detail?.variant?.id;
  if (variantId) renderLinks(variantId);
};

// Also rebuild once on load. On a market with a URL prefix (/pl, /fr, /en) Shopify
// rewrites server-rendered hrefs to shop files into `/pl/cdn/shop/files/…`, which
// 404s; the JSON map is not rewritten, so its URLs are the working ones.
const initial = document.querySelector('[data-instructions-links][data-variant-id]');
if (initial) renderLinks(initial.dataset.variantId);

document.addEventListener('variant-change', syncInstructionLink);
